/**
 * Gives OCI reviewers a source-record view of documents and conflict status.
 *
 * The first render uses the document and analysis summaries already available
 * in bulk. A reviewer opening one record loads only its details. Bulk CSV work
 * runs as one backend job, so browser traffic does not grow with award count.
 */
import { useMemo, useState, type FC } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Loader2, Search } from 'lucide-react'
import { toast } from 'sonner'

import { useServices } from '@/app-shell/services'
import { fetchDatasetByKey } from '@/applications/shared/services/dataset'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
} from '@/components/ui'
import type { NodeUserViewProps } from '@/core/types'
import { useAgents, type Agent, type WorkspaceRenderer } from '@/subsystems/agents'
import type { DocumentListItem } from '@/subsystems/documents'
import { listConnectorAnalyses } from '@/subsystems/vault/connectors/api'
import { ConnectorSyncStrip } from '@/subsystems/vault/connectors/components/ConnectorSyncStrip'
import { triggerBrowserDownload } from '@/subsystems/vault/services/browser-download'
import { waitForVaultJob } from '@/subsystems/vault/services/vault-jobs'

import { useRendererPreview } from '../../hooks/useRendererPreview'
import { useWorkspaceRuns } from '../../hooks/useWorkspaceRuns'
import type { DocumentListItemWithPipelines } from '../../models'
import { DashboardStatus } from '../DashboardStatus'
import { WorkspaceRunDialog } from '../WorkspaceRunDialog'
import { WorkspaceRunStatus } from '../WorkspaceRunStatus'
import { OciCsvDownloadDialog } from './OciCsvDownloadDialog'
import { OciRecordTable } from './OciRecordTable'
import { filterOciRecordGroupsFromDate } from './ociCsvExport'
import { Metric } from './OciDashboardIndicators'
import {
  fetchOciAttachments,
  fetchOciCsvExport,
  fetchOciDashboard,
  ociDashboardRecordsQueryKey,
  startOciCsvExport,
} from './ociDashboardApi'
import {
  buildOciDashboardRow,
  filterOciRecordGroups,
  hasCurrentSuccessfulAnalysis,
  sortOciRecordGroups,
  sortOciRows,
  type OciRecordSortKey,
  type OciDashboardRow,
  type OciDocumentReview,
  type OciFeedActivity,
  type SortDirection,
} from './ociDashboardModel'

const RECORDS_PER_PAGE = 20

function toWorkspaceDocument(
  review: OciDocumentReview,
): DocumentListItemWithPipelines {
  const { document } = review
  return {
    ...document,
    createdAt: document.latestVersionAt,
    latestSizeBytes: 0,
    processingStatus: 'complete',
    sourceProvider: null,
    externalId: null,
    externalIdLabel: null,
    externalVersion: null,
    artifactType: null,
    slotId: null,
    pipelineStatuses: [],
  }
}

function agentRendererContext(agent?: Agent): { rendererId: string; datasetKey: string } | null {
  for (const [rendererId, renderer] of Object.entries(agent?.renderers ?? {})) {
    for (const slot of renderer.slots) {
      if (slot.type === 'dataset' && slot.datasetKey) {
        return { rendererId, datasetKey: slot.datasetKey }
      }
    }
  }
  return null
}

export const OciDashboard: FC<NodeUserViewProps> = ({ orgId, workspaceId, agentId }) => {
  const apiClient = useServices().getClient()
  const agentsQuery = useAgents(apiClient, orgId, workspaceId)
  const agent = agentsQuery.data?.find(item => item.agentId === agentId)
  const rendererContext = useMemo(() => agentRendererContext(agent), [agent])
  const datasetKey = rendererContext?.datasetKey ?? null
  const renderers = useMemo<Record<string, WorkspaceRenderer>>(
    () => Object.fromEntries(
      Object.entries(agent?.renderers ?? {}).map(([id, renderer]) => [
        id,
        { ...renderer, agentId: agent?.agentId ?? '' },
      ]),
    ),
    [agent],
  )
  const [search, setSearch] = useState('')
  const [findingsOnly, setFindingsOnly] = useState(true)
  const [sortKey, setSortKey] = useState<OciRecordSortKey>('findings')
  const [direction, setDirection] = useState<SortDirection>('desc')
  const [pageIndex, setPageIndex] = useState(0)
  const [expandedRecordKey, setExpandedRecordKey] = useState<string | null>(null)
  const [expandedRecordType, setExpandedRecordType] = useState<string | null>(null)
  const [preparingRun, setPreparingRun] = useState<string | null>(null)
  const [runError, setRunError] = useState<string | null>(null)
  // cm-coding: An incomplete saved result normally tells a reviewer to rerun
  // the award. While its exact connector feed is already working, present that
  // live activity instead so the dashboard does not instruct a duplicate run.
  // React Query shares this request with ConnectorSyncStrip through the same
  // key, preserving one server read for both projections.
  const connectorAnalysesQuery = useQuery({
    queryKey: ['connector-analyses', orgId, workspaceId],
    queryFn: () => listConnectorAnalyses(apiClient, orgId, workspaceId),
    enabled: Boolean(orgId && workspaceId),
    refetchInterval: query => query.state.data?.some(item => (
      item.state === 'syncing' || item.state === 'running'
    )) ? 5_000 : 30_000,
  })
  const activityByFeedId = useMemo(() => {
    const activity = new Map<string, OciFeedActivity>()
    for (const analysis of connectorAnalysesQuery.data ?? []) {
      if (analysis.state === 'syncing') activity.set(analysis.feedId, 'syncing')
      if (analysis.state === 'running') activity.set(analysis.feedId, 'analyzing')
    }
    return activity
  }, [connectorAnalysesQuery.data])

  const dashboardQuery = useQuery({
    queryKey: [
      ...ociDashboardRecordsQueryKey(orgId, workspaceId),
      agent?.agentId,
      datasetKey,
    ],
    enabled: Boolean(agent && datasetKey),
    queryFn: () => fetchOciDashboard(
      apiClient, orgId, workspaceId, agent!.agentId, datasetKey!,
    ),
    // A user-started analysis finishes asynchronously after an award is already
    // visible. Refresh unfinished summaries until every result is current.
    refetchInterval: query => query.state.data?.records.some(record => (
      record.status === 'not-run' || record.status === 'stale'
    )) ? 5_000 : false,
  })
  const groups = dashboardQuery.data?.records ?? []
  const visibleGroups = useMemo(
    () => sortOciRecordGroups(
      filterOciRecordGroups(groups, search, findingsOnly),
      sortKey,
      direction,
    ),
    [direction, findingsOnly, groups, search, sortKey],
  )
  const pageCount = Math.max(1, Math.ceil(visibleGroups.length / RECORDS_PER_PAGE))
  const currentPageIndex = Math.min(pageIndex, pageCount - 1)
  const pageGroups = visibleGroups.slice(currentPageIndex * RECORDS_PER_PAGE, (currentPageIndex + 1) * RECORDS_PER_PAGE)
  const expandedGroup = pageGroups.find(group => group.key === expandedRecordKey) ?? null
  // cm-coding: Outer award expansion is a summary-only interaction; record
  // types and counts are already present in the lightweight business row. Load
  // document evidence only when a reviewer opens one of those record types, so
  // a simple disclosure never waits on hidden detail data. The application's
  // global previous-data placeholder remains disabled because evidence belongs
  // to one exact source record and must never cross an award-key transition.
  const detailsQuery = useQuery({
    queryKey: [
      'oci-dashboard-attachments', orgId, workspaceId, agent?.agentId,
      datasetKey, expandedGroup?.key,
    ],
    enabled: Boolean(agent && datasetKey && expandedGroup && expandedRecordType),
    queryFn: () => fetchOciAttachments(
      apiClient,
      orgId,
      workspaceId,
      agent!.agentId,
      datasetKey!,
      [expandedGroup!.key],
    ),
    staleTime: 60_000,
    placeholderData: undefined,
  })
  const detailedReviews = detailsQuery.data ?? []
  // cm-coding: Renderer preview needs the shared document contract only after
  // an award is expanded. Fill its presentation-neutral fields from the OCI
  // attachment summary instead of restoring the workspace-wide document read.
  const documents = useMemo<DocumentListItem[]>(
    () => detailedReviews.map(toWorkspaceDocument),
    [detailedReviews],
  )
  const pipelineIdsByRenderer = useMemo(
    () => agent?.pipelineId
      ? Object.fromEntries(Object.keys(agent.renderers).map(id => [id, agent.pipelineId!]))
      : {},
    [agent],
  )
  const preview = useRendererPreview({
    apiClient,
    orgId,
    workspaceId,
    renderers,
    documents,
    pipelineIdsByRenderer,
  })
  // cm-coding: Finding payloads belong to the same explicit record-type action
  // as document detail. Attachment identity is already useful once the compact
  // detail response arrives, so payload state enriches each stable row instead
  // of blocking or replacing the whole document list while S3 evidence loads.
  const payloadQueries = useQueries({
    queries: detailedReviews.filter(review => (
      (review.analysis.count ?? 0) > 0
      && (review.document.recordType ?? 'Document') === expandedRecordType
    )).map(({ document, analysis: status }) => {
      const version = status.version ?? document.latestVersion
      return {
        queryKey: ['oci-dashboard-payload', orgId, workspaceId, document.documentId, datasetKey, version],
        queryFn: () => fetchDatasetByKey(
          apiClient,
          orgId,
          workspaceId,
          document.documentId,
          agent?.agentId ?? '',
          datasetKey!,
          version,
        ),
        enabled: Boolean(datasetKey),
        staleTime: 60_000,
        // cm-coding: Findings belong to the document that produced them. Show a
        // loading state when the selected document changes instead of retaining
        // evidence from the prior document under the new document's heading.
        placeholderData: undefined,
      }
    }),
  })
  const reviewsWithPayloads = detailedReviews.filter(review => (
    (review.analysis.count ?? 0) > 0
    && (review.document.recordType ?? 'Document') === expandedRecordType
  ))
  const payloads = new Map(reviewsWithPayloads.map((review, index) => {
    const query = payloadQueries[index]
    return [review.document.documentId, {
      payload: query?.data?.payload,
      state: query?.isError ? 'error' as const : query?.isPending ? 'loading' as const : 'ready' as const,
    }]
  }))
  const expandedRows = sortOciRows(detailedReviews.map(({ document, analysis: status }) => (
    buildOciDashboardRow(
      document,
      status,
      payloads.get(document.documentId)?.payload,
      payloads.get(document.documentId)?.state ?? 'ready',
    )
  )), 'findings', 'desc')
  const metrics = dashboardQuery.data ?? { documentsWithFindings: 0, findings: 0 }
  const pendingGroups = groups.filter(group => (
    group.status !== 'clear'
    && group.status !== 'flagged'
    && !(group.feedId && activityByFeedId.has(group.feedId))
  ))
  const refreshReview = () => {
    void dashboardQuery.refetch()
    if (expandedGroup && expandedRecordType) void detailsQuery.refetch()
  }
  const runs = useWorkspaceRuns({
    apiClient,
    orgId,
    workspaceId,
    agents: agent ? [agent] : [],
    documents: [],
    onStatusChange: refreshReview,
    onAllComplete: erroredJobIds => {
      refreshReview()
      setRunError(erroredJobIds.length > 0 ? 'One or more analysis runs failed.' : null)
    },
    onError: setRunError,
  })

  const resetView = () => {
    setPageIndex(0)
    setExpandedRecordKey(null)
    setExpandedRecordType(null)
  }
  const toggleRecord = (key: string) => {
    setExpandedRecordKey(current => current === key ? null : key)
    setExpandedRecordType(null)
  }
  const handleSort = (next: OciRecordSortKey) => {
    if (next === sortKey) setDirection(current => current === 'asc' ? 'desc' : 'asc')
    else {
      setSortKey(next)
      setDirection(next === 'record' ? 'asc' : 'desc')
    }
    resetView()
  }
  const openRow = (row: OciDashboardRow) => {
    const document = documents.find(item => item.documentId === row.documentId)
    if (document && rendererContext) {
      preview.openVersionForRenderer(
        document,
        rendererContext.rendererId,
        row.version,
      )
    }
  }

  const prepareRun = async (
    recordKeys: string[],
    progressKey: string,
    pendingOnly = false,
  ) => {
    if (!agent || !datasetKey || recordKeys.length === 0) return
    setPreparingRun(progressKey)
    setRunError(null)
    try {
      const reviews = await fetchOciAttachments(
        apiClient,
        orgId,
        workspaceId,
        agent.agentId,
        datasetKey,
        recordKeys,
      )
      const reviewsToRun = pendingOnly
        ? reviews.filter(review => !hasCurrentSuccessfulAnalysis(review))
        : reviews
      if (reviewsToRun.length === 0) {
        setRunError('No documents are available to analyze.')
        return
      }
      runs.openDocuments([agent.agentId], reviewsToRun.map(toWorkspaceDocument))
    } catch {
      setRunError('Could not load the documents for this analysis run.')
    } finally {
      setPreparingRun(null)
    }
  }

  if (agentsQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!agent || !rendererContext || !datasetKey) {
    return <p className="text-sm text-destructive">OCI dashboard is missing its agent dataset configuration.</p>
  }

  const downloadRecordsCsv = async (fromDate: string | null): Promise<boolean> => {
    const exportGroups = filterOciRecordGroupsFromDate(visibleGroups, fromDate)
    if (exportGroups.length === 0) {
      toast.error('No awards match that date', {
        description: 'Choose an earlier date or leave the date blank.',
      })
      return false
    }
    try {
      const job = await startOciCsvExport(
        apiClient,
        orgId,
        workspaceId,
        agent.agentId,
        datasetKey,
        exportGroups.map(group => group.key),
      )
      await waitForVaultJob(apiClient, orgId, workspaceId, job.jobId)
      const result = await fetchOciCsvExport(
        apiClient, orgId, workspaceId, job.jobId,
      )
      triggerBrowserDownload(result.url, result.filename)
      return true
    } catch {
      toast.error('CSV download failed', {
        description: 'Could not load every OCI finding. No partial file was downloaded.',
      })
      return false
    }
  }

  const loading = dashboardQuery.isPending
  return (
    <div className="space-y-4" data-testid="oci-agent-dashboard">
      <ConnectorSyncStrip
        api={apiClient}
        orgId={orgId}
        workspaceId={workspaceId}
        pending={{
          count: pendingGroups.length,
          singular: 'award',
          plural: 'awards',
          running: preparingRun === 'all',
          onRun: () => void prepareRun(
            pendingGroups.map(group => group.key),
            'all',
            true,
          ),
        }}
      />
      <div className="grid grid-cols-2 gap-3">
        <Metric
          label="Findings"
          value={metrics.documentsWithFindings}
          tone="danger"
          testId="oci-metric-findings"
        />
        <Metric
          label="Reported findings"
          value={metrics.findings}
          tone="danger"
          testId="oci-metric-reported-findings"
        />
      </div>
      <Card>
        <CardHeader className="gap-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>OCI Award Review</CardTitle>
              <CardDescription>
                Awards are paged as summaries. Expand one to see its record types.
              </CardDescription>
            </div>
            <OciCsvDownloadDialog
              disabled={visibleGroups.length === 0}
              onDownload={downloadRecordsCsv}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-64 max-w-xl flex-1">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                data-testid="oci-awards-filter"
                value={search}
                onChange={event => {
                  setSearch(event.target.value)
                  resetView()
                }}
                placeholder="Filter awards"
                aria-label="Filter awards"
              />
            </div>
            <div className="flex items-center gap-1" role="group" aria-label="OCI review filter">
              <Button
                size="sm"
                variant={findingsOnly ? 'default' : 'outline'}
                data-testid="oci-findings-filter"
                aria-pressed={findingsOnly}
                onClick={() => {
                  setFindingsOnly(true)
                  resetView()
                }}
              >
                Findings only
              </Button>
              <Button
                size="sm"
                variant={!findingsOnly ? 'default' : 'outline'}
                aria-pressed={!findingsOnly}
                onClick={() => {
                  setFindingsOnly(false)
                  resetView()
                }}
              >
                All awards
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="px-6">
            <DashboardStatus error={runError} />
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : visibleGroups.length === 0 ? (
            <p className="px-6 py-16 text-center text-sm text-muted-foreground">
              {groups.length === 0 ? 'No awards have been imported.' : 'No awards match this filter.'}
            </p>
          ) : (
            <OciRecordTable
              groups={pageGroups}
              expandedRecordKey={expandedGroup?.key ?? null}
              expandedRecordType={expandedRecordType}
              expandedRows={expandedRows}
              activityByFeedId={activityByFeedId}
              dataLoading={detailsQuery.isPending}
              dataError={detailsQuery.isError}
              sortKey={sortKey}
              direction={direction}
              pageIndex={currentPageIndex}
              pageCount={pageCount}
              totalRecords={visibleGroups.length}
              pageSize={RECORDS_PER_PAGE}
              onSort={handleSort}
              onToggle={toggleRecord}
              onToggleRecordType={recordType => setExpandedRecordType(current => (
                current === recordType ? null : recordType
              ))}
              onOpenRow={openRow}
              preparingRecordKey={preparingRun}
              onRun={group => void prepareRun([group.key], group.key)}
              onPageChange={nextPage => {
                setPageIndex(nextPage)
                setExpandedRecordKey(null)
                setExpandedRecordType(null)
              }}
            />
          )}
        </CardContent>
      </Card>
      <WorkspaceRunStatus entries={runs.entries} onClear={runs.clearEntries} />
      <WorkspaceRunDialog
        open={runs.pending !== null}
        agents={runs.pendingAgents}
        documents={runs.pendingDocuments}
        running={runs.isRunning}
        onOpenChange={open => { if (!open) runs.close() }}
        onConfirm={versions => void runs.confirm(versions)}
      />
      {preview.dialogs}
    </div>
  )
}
